import React from 'react';
import KI from '../theme/palette';

// Self-contained SVG line chart for "Ventes / jour" — no external charting dep.
// Bigger "case", a montant (Y) + jours (X) échelle around the dark panel, and a
// 3D look: glowing curves, translucent area fills, a beveled navy panel and a
// solid dot at each line's latest point. Rendered static — no animations.
//
// Expected `series` shape (from the dashboard API): an array of
//   { date: 'YYYY-MM-DD', ca: number, encaisse: number, benefice: number }
// Brand Kit §05 (2026-09-04) : CA vert #15803D 2px · Coût rouge #DC2626 1.5px ·
// Bénéfice amber #F59E0B 1.5px (épaisseurs du mode clair).
const LINES = [
  { key: 'ca', label: 'CA', color: KI.spruce, width: 2 },
  { key: 'capital', label: 'Coût', color: KI.coralDark, width: 1.5 }, // coût des ventes / jour
  { key: 'benefice', label: 'Bénéfice', color: KI.gold, width: 1.5 },
];

// viewBox units. The SVG scales uniformly to the container width (height follows
// the aspect ratio), so circles stay round and the glow stays clean.
const W = 1000;
// H (hauteur du viewBox) est une PROP `height` (défaut 460) : la hauteur rendue
// vaut largeur × H/W. Une carte pleine largeur (Statistiques de ventes) passe
// une valeur plus basse pour ne pas rendre un panneau deux fois plus haut que
// celui du tableau de bord (owner 2026-09-07). PLOT est calculé dans le
// composant, à partir de H.
const M = { top: 22, right: 22, bottom: 46, left: 70 }; // margins hold the échelle

// Compact amount for the Y échelle (3-decimal DT would be unreadable here).
const fmtTick = (n) => {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(a % 1_000_000 ? 1 : 0)}M`;
  if (a >= 1_000) return `${(v / 1_000).toFixed(a % 1_000 ? 1 : 0)}k`;
  return `${Math.round(v)}`;
};

// DD/MM for the X échelle.
const fmtDay = (iso) => {
  const p = String(iso || '').split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}` : String(iso || '');
};

// "Nice" rounded number for human-friendly axis ticks.
const niceNum = (range, round) => {
  const exp = Math.floor(Math.log10(range || 1));
  const f = (range || 1) / 10 ** exp;
  let nf;
  if (round) nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  else nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * 10 ** exp;
};

// Hard, straight segments between days (sharp zig-zag — no smoothing).
const linePath = (pts) => {
  if (pts.length < 2) return '';
  return pts.map((p, i) => `${i ? 'L' : 'M'} ${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
};

const SalesTrendChart = ({ series, transparent = false, light = false, legend = 'top', height = 460 }) => {
  const H = height;
  const PLOT = { x: M.left, y: M.top, w: W - M.left - M.right, h: H - M.top - M.bottom };
  const data = Array.isArray(series) ? series : [];

  if (data.length < 2) {
    return (
      <div className={`flex flex-col items-center justify-center h-72 rounded-2xl ${
        light ? 'bg-gray-50 text-gray-400' : 'text-gray-400'
      }`} style={light ? undefined : { background: KI.darkBg }}>
        <p className="text-sm">Pas assez de données pour tracer la courbe</p>
      </div>
    );
  }

  // `light` : pas de boîtier sombre — courbes fines sans halo ni aplat, grille
  // pointillée #F3F4F6, échelles 10.5px #9CA3AF (Composants-2 §12).
  const gridStroke = light ? KI.ramps.gray[100] : KI.pureWhite;
  const gridOp = (t) => (t === 0 ? (light ? 1 : 0.32) : (light ? 1 : 0.14));
  const gridDash = '4 6';
  const tickFill = light ? KI.faint : KI.ramps.gray[400];
  const tickSize = 15;
  const flat = transparent || light; // pas de glow / aplat (mobile + cartes blanches)

  // Shared, "nice" scale across the 3 series; floor at 0 unless bénéfice is negative.
  const vals = data.flatMap((d) => LINES.map((l) => Number(d[l.key]) || 0));
  const rawMax = Math.max(1, ...vals);
  const rawMin = Math.min(0, ...vals);
  const step = niceNum(niceNum(rawMax - rawMin, false) / 4, true) || 1;
  const yMin = Math.floor(rawMin / step) * step;
  const yMax = Math.ceil(rawMax / step) * step;
  const ticks = [];
  for (let v = yMin; v <= yMax + step / 2; v += step) ticks.push(v);

  const x = (i) => PLOT.x + (i * PLOT.w) / (data.length - 1);
  const y = (v) => PLOT.y + PLOT.h * (1 - (v - yMin) / (yMax - yMin || 1));
  const baseY = y(0);

  const ptsFor = (key) => data.map((d, i) => [x(i), y(Number(d[key]) || 0)]);

  // ~6 day labels max so the X échelle never crowds.
  const every = Math.max(1, Math.round((data.length - 1) / 5));
  const showDay = (i) => i % every === 0 || i === data.length - 1;

  // Légende CA · Coût · Bénéfice — puce + dernière valeur de chaque série.
  // Brand Kit §05 la place AU-DESSUS du graphe : c'est le défaut (`legend="top"`),
  // gardé partout ailleurs. `legend="bottom"` la fait passer SOUS la courbe —
  // demandé par le propriétaire (2026-09-07) pour la case « Ventes / jour » du
  // tableau de bord — placement repris de la PROD : sous le graphe et CENTRÉ
  // (`mt-3 justify-center`). Puces 9px rayon 2, 11.5px #6B7280, gap 12.
  const legendRow = (
    <div className={`flex flex-wrap items-center gap-x-6 gap-y-1 ${legend === 'bottom' ? 'mt-3 justify-center' : 'mb-3'}`}>
      {LINES.map((l) => {
        const last = Number(data[data.length - 1]?.[l.key]) || 0;
        return (
          <span key={l.key} className="inline-flex items-center gap-1.5 text-xs text-gray-500">
            <span className="w-3 h-1.5 rounded-full" style={{ background: l.color }} />
            {l.label}
            <span className="font-semibold text-gray-900 tabular-nums">{fmtTick(last)}</span>
          </span>
        );
      })}
    </div>
  );

  return (
    <div>
      {legend !== 'bottom' && legendRow}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Courbe des ventes par jour : chiffre d'affaires, encaissé et bénéfice, avec échelle montant et jours"
      >
        <defs>
          {/* Beveled navy panel + top sheen for depth */}
          <linearGradient id="kstc-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={KI.darkCard} />
            <stop offset="1" stopColor={KI.black} />
          </linearGradient>
          <radialGradient id="kstc-sheen" cx="0.5" cy="0" r="0.95">
            <stop offset="0" stopColor={KI.pureWhite} stopOpacity="0.12" />
            <stop offset="1" stopColor={KI.pureWhite} stopOpacity="0" />
          </radialGradient>
          {LINES.map((l) => (
            <linearGradient key={l.key} id={`kstc-fill-${l.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={l.color} stopOpacity="0.42" />
              <stop offset="1" stopColor={l.color} stopOpacity="0" />
            </linearGradient>
          ))}
          {LINES.map((l) => (
            <filter key={l.key} id={`kstc-glow-${l.key}`} x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor={l.color} floodOpacity="0.9" />
            </filter>
          ))}
          {/* Stronger glow for the latest-day focal dots */}
          {LINES.map((l) => (
            <filter key={`p${l.key}`} id={`kstc-pglow-${l.key}`} x="-80%" y="-80%" width="260%" height="260%">
              <feDropShadow dx="0" dy="0" stdDeviation="9" floodColor={l.color} floodOpacity="1" />
            </filter>
          ))}
          <clipPath id="kstc-clip">
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h} rx="0" />
          </clipPath>
        </defs>

        {/* The "case": dark beveled panel — skipped when `transparent` (mobile)
            or `light` (stats page on white). */}
        {!transparent && !light && (
          <>
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h} rx="0" fill="url(#kstc-bg)" />
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h} rx="0" fill="url(#kstc-sheen)" />
            <rect x={PLOT.x} y={PLOT.y} width={PLOT.w} height={PLOT.h} rx="0"
              fill="none" stroke={KI.pureWhite} strokeOpacity="0.10" strokeWidth="1"
              vectorEffect="non-scaling-stroke" />
            {/* bevel highlight along the top edge */}
            <line x1={PLOT.x + 14} y1={PLOT.y + 1} x2={PLOT.x + PLOT.w - 14} y2={PLOT.y + 1}
              stroke={KI.pureWhite} strokeOpacity="0.18" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          </>
        )}

        {/* Y échelle (montant) — grid lines + left labels */}
        {ticks.map((t, i) => (
          <g key={`t${i}`}>
            <line x1={PLOT.x} y1={y(t)} x2={PLOT.x + PLOT.w} y2={y(t)}
              stroke={gridStroke} strokeOpacity={gridOp(t)}
              strokeDasharray={t === 0 ? '0' : gridDash}
              vectorEffect="non-scaling-stroke" />
            <text x={PLOT.x - 12} y={y(t) + 4} textAnchor="end"
              fontSize={tickSize} fill={tickFill}>{fmtTick(t)}</text>
          </g>
        ))}
        {/* X échelle (jours) — labels under the black panel */}
        {data.map((d, i) =>
          showDay(i) ? (
            <text key={`d${i}`} x={x(i)} y={H - M.bottom + 26} textAnchor="middle"
              fontSize={tickSize} fill={tickFill}>{fmtDay(d.date)}</text>
          ) : null
        )}

        {/* Series: area fills (depth) then glowing curves (clipped to the panel).
            Mode clair : courbes fines seules. */}
        <g clipPath="url(#kstc-clip)">
          {LINES.map((l) => {
            const pts = ptsFor(l.key);
            const line = linePath(pts);
            const area = `${line} L ${pts[pts.length - 1][0].toFixed(1)},${baseY.toFixed(1)} L ${pts[0][0].toFixed(1)},${baseY.toFixed(1)} Z`;
            return (
              <g key={l.key}>
                {!light && <path d={area} fill={`url(#kstc-fill-${l.key})`} />}
                {/* Pas de glow sur mobile (transparent) — Ahmed 2026-08-17. */}
                <path d={line} fill="none"
                  stroke={l.color} strokeWidth="3" strokeLinejoin="miter" strokeLinecap="butt"
                  filter={flat ? undefined : `url(#kstc-glow-${l.key})`} vectorEffect="non-scaling-stroke" />
              </g>
            );
          })}
        </g>

        {/* Focal point: solid dot at each line's latest value */}
        {LINES.map((l) => {
          const pts = ptsFor(l.key);
          const [cx, cy] = pts[pts.length - 1];
          return (
            <circle key={`p${l.key}`} cx={cx} cy={cy} r="5" fill={l.color}
              stroke={KI.pureWhite} strokeWidth="2" vectorEffect="non-scaling-stroke"
              filter={flat ? undefined : `url(#kstc-pglow-${l.key})`} />
          );
        })}
      </svg>
      {legend === 'bottom' && legendRow}
    </div>
  );
};

export default SalesTrendChart;
